<template>
    <v-menu :disabled="disabled" v-model="isMenuOpen" transition="slide-y-transition" bottom left offset-y open-on-hover :close-delay="200" :close-on-click="false" :close-on-content-click="false" min-width="36%!important" max-width="60%" content-class="myprovet-menu-card top-distance">
        <template v-slot:activator="{ on, attrs }">
            <slot v-bind:event="{ on, attrs }" />
        </template>

        <v-card class="myprovet-menu-card-inner-no-shadow" data-cy="navbar-client-menu" width="100%">
            <v-card-title class="d-flex justify-center align-center">
                <v-text-field
                    v-if="userStore.hasPermissionTo('Search branches by client_number')"
                    :value="searchBranchID"
                    type="number"
                    dense
                    solo
                    flat
                    :placeholder="$t('clientMenu.number')"
                    prepend-inner-icon="mdi-counter"
                    hide-details
                    height="40px"
                    :readonly="isLoading"
                    :background-color="isLoading ? 'rgb(200,200,200)' : '#F4F7FA'"
                    class="font-size-sm input-bg-color-gray dark-gray-color mr-2 navbar-number client-number-field"
                    style="border-radius: 8px"
                    :clearable="!isLoading"
                    @beforeinput="validateInput"
                    @input="checkIntegerNumber"
                >
                    <template v-slot:append v-if="(searchBranchID && isLoading) || !searchBranchID">
                        <div class="navbar-branch-selector-append-padding"></div>
                    </template>
                </v-text-field>

                <v-text-field
                    v-if="userStore.hasPermissionTo('Search branches by name')"
                    v-model="searchBranchName"
                    dense
                    solo
                    flat
                    :placeholder="$t('clientMenu.name')"
                    prepend-inner-icon="mdi-rename-box"
                    hide-details
                    height="40px"
                    :readonly="isLoading"
                    :background-color="isLoading ? 'rgb(200,200,200)' : '#F4F7FA'"
                    class="font-size-sm input-bg-color-gray dark-gray-color mr-2 name-field"
                    style="border-radius: 8px"
                    :clearable="!isLoading"
                    @input="search(!searchBranchName)"
                >
                    <template v-slot:append v-if="(searchBranchName && isLoading) || !searchBranchName"> <div class="navbar-branch-selector-append-padding"></div> </template
                    ></v-text-field>

                <v-text-field
                    v-if="userStore.hasPermissionTo('Search branches by taxpayer number')"
                    v-model="searchBranchTaxpayerNumber"
                    dense
                    solo
                    flat
                    :placeholder="$t('clientMenu.vat')"
                    prepend-inner-icon="mdi-circle-multiple-outline"
                    hide-details
                    height="40px"
                    :readonly="isLoading"
                    :background-color="isLoading ? 'rgb(200,200,200)' : '#F4F7FA'"
                    class="font-size-sm input-bg-color-gray dark-gray-color nif-field"
                    style="border-radius: 8px"
                    :clearable="!isLoading"
                    @input="search(!searchBranchTaxpayerNumber)"
                >
                    <template v-slot:append v-if="(searchBranchTaxpayerNumber && isLoading) || !searchBranchTaxpayerNumber"> <div class="navbar-branch-selector-append-padding"></div> </template
                    ></v-text-field>
            </v-card-title>

            <v-card-text class="px-0" v-bind:class="{ 'myprovet-menu-card-scroll': isScrollable }">
                <v-list flat>
                    <v-list-item-group>
                        <div ref="beginning"></div>

                        <template v-for="(item, index) in getGroupOfBranches">
                            <menu-item
                                :key="index"
                                :icon="item.children ? 'mdi-alpha-c' : getBranchIcon(item)"
                                :parent="item.children ? true : false"
                                :name="item.name"
                                :orange="item.is_petfood && !item.children"
                                :id="item.id"
                                ref="branchMenuItems"
                                :active="isActiveSelection(item)"
                                @click="onBranchSelection(item)"
                            >
                            </menu-item>

                            <v-divider v-if="index < branches.length - 1" :key="`${index}-divider`" class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>

                            <template v-if="item.children">
                                <div v-for="child in item.children" :key="child.client_number + '_child_' + child.company_branch">
                                    <menu-item class="pl-16" :icon="getBranchIcon(child)" :name="child.name" :orange="child.is_petfood && !child.children" :id="child.id" :active="isActiveSelection(child)" @click="onBranchSelection(child)"> </menu-item>

                                    <v-divider class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                                </div>
                            </template>
                        </template>
                    </v-list-item-group>
                </v-list>

                <v-lazy v-if="!isLoading">
                    <div v-intersect="updatePagination"></div>
                </v-lazy>

                <div v-else class="text-center">
                    <v-progress-circular class="dark-blue-color" indeterminate></v-progress-circular>
                </div>
            </v-card-text>

            <div class="navbar-clients-total-amount">
                <span class="dark-blue-color" style="font-size: 0.75rem">{{ $t('clientMenu.resultsCount', { shown: branches.length, total: totalResults }) }}</span>
            </div>
        </v-card>
    </v-menu>
</template>

<script>
import { mapStores } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useUserStore } from '@/Stores/user';
import { useNormalOrderStore } from '@/Stores/normalorder';
import { useNotificationsStore } from '@/Stores/notifications';
import { axios } from '@/Services/Backend';
import { buildQueryObjectFromModel } from '@arentia/atools/QueryBuilder';
import MenuItem from './AppBarClientMenuItem.vue';
import { showSuccess } from '@/Mixins/HelperFunctions';

export default {
    props: {
        isScrollable: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    components: {
        MenuItem,
    },

    data() {
        return {
            isMenuOpen: false,

            searchBranchID: null,
            searchBranchName: null,
            searchBranchTaxpayerNumber: null,

            branches: [],

            page: 1,
            lastPage: 1,
            isLoading: false,
            totalResults: null,

            _timer: null,
        };
    },

    /// We want to get the branches that the user has access to and their selected branch (if it exists).
    async created() {
        await this.userStore.getUser(); // Because inertiajs is lacking a before() route functionality, we'll also get the user here.
        this.getBranches();

        axios
            .get('/api/user/branch')
            .then(({ data }) => {
                this.branchStore.setBranch(data);

                // TODO: Put this in the right place, so that it also respects branch changes while in the page and is called again
                this.notificationsStore.subscribeSharedCart(data.id, null, this.userStore.user?.id, this.userStore.user?.seller_id);
            })
            .catch((error) => {
                console.error(error);
                this.showError(this.$t('clientMenu.loadClientsError'));
            });
    },

    watch: {
        isMenuOpen() {
            // We don't need to do anything when the menu opens, only when it closes.
            if (this.isMenuOpen) return;
            if (this.searchBranchID !== null || this.searchBranchName !== null || this.searchBranchTaxpayerNumber !== null) {
                // If the menu is closed and the branch had a search, clear it.
                this.$nextTick(() => this.clearFilters());
            }
            // Scroll to the beginning of the list with a small delay to hide the change while the menu is closing.
            setTimeout(() => this.$refs.beginning.scrollIntoView(true), 200);
        },
    },

    computed: {
        ...mapStores(useBranchStore, useUserStore, useNormalOrderStore, useNotificationsStore),

        getGroupOfBranches() {
            if (this.branches.length === 0) return [];

            // If company A and B have an ID of e.g. '40' (they're different branches of the same company)
            //  we want them to be grouped together with the main branch being the root node and the other branches the children nodes.
            const seenBranch = new Map();
            const result = [];

            for (const branch of this.branches) {
                if (seenBranch.has(branch.client_number)) continue;

                // Find all of the branches of the company.
                const branches = this.branches.filter((b) => b.client_number === branch.client_number);

                if (branches.length === 1) {
                    // Only one company branch (i.e. the main branch)
                    result.push(branch);
                } else {
                    // Multiple branches (i.e. add the consolidated company entry and all of the branches inside)
                    result.push({
                        name: 'Filiais do cliente ' + branch.main_company_branch.name,
                        children: branches,
                    });
                }

                seenBranch.set(branch.client_number, true);
            }

            return result;
        },
    },

    mounted() {
        window.EventBus.$on('refresh-shared-cart', ({ pendingCartId, leavingPendingCartMode }) => {
            // If a pendingCartId is received, do a slightly different process from now on
            this.getShoppingCart(this.branchStore?.branch, pendingCartId, leavingPendingCartMode);
        });

        window.EventBus.$on('new-shared-cart', ({ data, branchId }) => {
            // The branch check is already being done later in the process, but an additional one is made here to optimize the execution
            if (data[1] == this.userStore.user?.id || branchId == this.branchStore?.branch) {
                return;
            }

            this.getShoppingCart(this.branchStore?.branch);
        });
        window.EventBus.$on('refresh-shared-cart-subscription', ({ prevBranchId }) => {
            // If a pendingCartId is received, do a slightly different process from now on
            this.refreshSharedCartSubscription(prevBranchId);
        });
    },

    beforeDestroy() {
        window.EventBus.$off('refresh-shared-cart');
        window.EventBus.$off('new-shared-cart');
        window.EventBus.$off('refresh-shared-cart-subscription');
    },

    methods: {
        refreshSharedCartSubscription(prevBranchId = null) {
            this.notificationsStore.subscribeSharedCart(this.branchStore?.branch?.id, prevBranchId, this.userStore.user?.id, this.userStore.user?.seller_id);
        },

        getShoppingCart(branch, pendingCartId = null, leavingPendingCartMode = false) {
            const clientNumber = branch?.client_number;
            const companyBranch = branch?.company_branch;

            // Obtain the shopping card if this is not a pending cart and we are not forcing the request (by sending a pendingCartId)
            if (this.normalOrderStore.getIsPendingCart && pendingCartId <= 0) return;

            if (this.$page.url == `/orders/normal/summary/${clientNumber}/${companyBranch}`) {
                // Only warn the user if not in the pending cart mode, as that already disables the shared carts
                if (!this.normalOrderStore.getIsPendingCart) {
                    this.showWarning(this.$t('clientMenu.noRemoteCartOnCheckout'));
                }
                return;
            }

            if (!branch || !branch.id || this.normalOrderStore.getIsLoadingSharedCart) return;

            this.normalOrderStore.setIsLoadingSharedCart(true);

            let path;
            if (pendingCartId !== null && pendingCartId !== -1) {
                path = '/api/orders/pending-carts/' + pendingCartId;
            } else {
                path = '/api/shopping-carts/branch/' + branch.id;
            }

            axios
                .get(path)
                .then(({ data }) => {
                    if (data.disabled === true) {
                        this.normalOrderStore.setIsLoadingSharedCart(false);
                        return;
                    }
                    this.processNewShoppingCart({ data, branchId: branch.id, leavingPendingCartMode });
                })
                .catch((error) => {
                    console.error(error);
                    this.normalOrderStore.setIsLoadingSharedCart(false);
                    if (pendingCartId) {
                        this.showError(this.$t('clientMenu.loadPendingCartError'));
                    } else {
                        this.showError(this.$t('clientMenu.loadLatestCartError'));
                    }
                });
        },

        processNewShoppingCart({ data, branchId = null, leavingPendingCartMode = false }) {
            if (branchId) {
                if (branchId != this.branchStore.branch?.id) {
                    // The response is for a previous branch. Release the loading flag (otherwise it stays stuck and
                    // no cart is ever loaded/sent again) and load the cart of the branch that is selected now.
                    this.normalOrderStore.setIsLoadingSharedCart(false);
                    this.getShoppingCart(this.branchStore.branch);
                    return;
                }
            }

            // Processing is complete, now to send all data to localStorage
            this.normalOrderStore.saveOtherDataFromSharedCart(data[0]);
            this.normalOrderStore.saveCampaignsSharedCart(data[0].campaigns_json ?? '[]');
            this.normalOrderStore.saveCartSharedCart(data[0].products_json ?? '[]');

            useNormalOrderStore().reloadFromSharedCart(leavingPendingCartMode);
        },


        getBranchIcon(branch) {
            if (!branch) return '';
            return branch.is_petfood ? 'mdi-alpha-p' : 'mdi-alpha-f';
        },

        isActiveSelection(item) {
            if (!item.client_number) return false; // @Note: Consolidated branches are never selected.

            return this.branchStore.branch?.id === item.id;
        },

        onBranchSelection(branch) {
            if (!branch.client_number) {
                return;
            }
            this.branchStore.setBranchNotify(branch, this.$inertia, this.userStore.user?.id, this.userStore.user?.seller_id);
            this.isMenuOpen = false;
            //console.log('onBranchSelection');
            setTimeout(()=>{
                window.EventBus.$emit('update:profiles-list', true)
            },500)

        },

        clearFilters() {
            this.searchBranchID = null;
            this.searchBranchName = null;
            this.searchBranchTaxpayerNumber = null;

            this.search(true);
        },

        getBranches() {
            this.isLoading = true;

            const schema = {
                id: { dataFieldOperator: 'begins_with' },
                name: { dataFieldOperator: 'keywords' },
                taxpayer_number: { dataFieldOperator: 'contains' },
            };

            const search = { id: this.searchBranchID, name: this.searchBranchName, taxpayer_number: this.searchBranchTaxpayerNumber };
            const query = JSON.stringify(buildQueryObjectFromModel(search, schema));

            return axios
                .get(`/api/branches/search?page=${this.page}&queryObject=${query}`)
                .then((response) => {
                    this.isLoading = false;
                    this.page = response.data.meta.current_page;
                    this.lastPage = response.data.meta.last_page;
                    this.totalResults = response.data.meta.total;
                    this.branches = [...this.branches, ...response.data.data];
                })
                .catch((error) => {
                    this.isLoading = false;

                    console.log(error);
                });
        },

        checkIntegerNumber(value) {
            if (value) {
                value = parseInt(value);
                if (!Number.isInteger(value)) return;
            }

            this.searchBranchID = value;
            this.search(!value);
        },

        selectDefaultBranch() {
            if (this.branches.length === 0) {
                return;
            }

            const firstBranch = this.branches[0];
            this.branchStore.setBranchNotify(firstBranch, this.$inertia, this.userStore.user?.id, this.userStore.user?.seller_id);
        },

        search(skipDebounce) {
            this.branches = [];
            this.page = 1;
            this.lastPage = 1;

            // Debounce the search - it will prevent errors when updating too quickly.
            clearTimeout(this._timer);

            this._timer = setTimeout(
                () => {
                    this.isLoading = true;
                    this.getBranches();
                },
                skipDebounce ? 0 : 1000
            );
        },

        updatePagination() {
            if (this.isLoading || this.page >= this.lastPage) return;

            this.page += 1;
            this.getBranches();
        },
    },
};
</script>

<style scoped>
.navbar-clients-total-amount {
    float: right;
    padding-right: 16px;
    padding-top: 5px;
    padding-bottom: 5px;
}
</style>

<style>
.myprovet-menu-card-inner-no-shadow {
    box-shadow: none !important;
}
</style>
